import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { orderService, CreateOrderData } from '../services/orderService';
import { formatPrice } from '../utils';
import Breadcrumbs from '../components/UI/Breadcrumbs';

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { items, totalAmount, clearCart } = useCart();
  const { user, isAuthenticated } = useAuth();
  const toast = useToast();

  const [step, setStep] = useState(1); // 1: Address, 2: Payment, 3: Confirm
  const [isProcessing, setIsProcessing] = useState(false);

  const [formData, setFormData] = useState({
    fullName: user?.name || '',
    phone: user?.phone || '',
    address: '',
    county: '',
    town: '',
    deliveryMethod: 'delivery' as 'delivery' | 'pickup',
    paymentMethod: 'mpesa' as 'mpesa' | 'cash' | 'card',
    mpesaPhone: user?.phone || '',
    notes: '',
  });

  const deliveryFee = formData.deliveryMethod === 'delivery' && totalAmount < 2000 ? 200 : 0;
  const finalTotal = totalAmount + deliveryFee;

  const counties = [
    'Nairobi', 'Kiambu', 'Nakuru', 'Meru', 'Kisumu', 'Mombasa', 'Eldoret', 
    'Kakamega', 'Nyeri', 'Machakos', 'Other'
  ];

  if (!isAuthenticated) {
    navigate('/login');
    return null;
  }

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePlaceOrder = async () => {
    setIsProcessing(true);

    try {
      const orderData: CreateOrderData = {
        products: items.map(item => ({
          productId: item.product._id,
          quantity: item.quantity,
          price: item.product.price,
        })),
        deliveryAddress: {
          fullName: formData.fullName,
          phone: formData.phone,
          address: formData.address,
          county: formData.county,
          town: formData.town,
        },
        deliveryMethod: formData.deliveryMethod,
        paymentMethod: formData.paymentMethod,
        notes: formData.notes,
      };

      const response = await orderService.createOrder(orderData);

      if (response.success && response.data) {
        const orderId = response.data._id;

        // If M-Pesa, initiate payment
        if (formData.paymentMethod === 'mpesa') {
          const paymentResponse = await orderService.initiateMpesaPayment(
            orderId,
            formData.mpesaPhone
          );

          if (paymentResponse.success) {
            toast.success('M-Pesa payment request sent! Check your phone.');
            clearCart();
            navigate(`/order-confirmation/${orderId}`);
          } else {
            toast.error('Payment initiation failed: ' + paymentResponse.error);
          }
        } else {
          toast.success('Order placed successfully!');
          clearCart();
          navigate(`/order-confirmation/${orderId}`);
        }
      } else {
        toast.error('Failed to place order: ' + response.error);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={[
          { label: 'Home', path: '/' },
          { label: 'Cart', path: '/cart' },
          { label: 'Checkout' },
        ]} />

        <h1 className="text-4xl font-bold text-gray-900 mb-8">Checkout</h1>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {s}
                </div>
                {s < 3 && <div className={`w-16 h-1 ${step > s ? 'bg-green-600' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
          <div className="flex justify-between max-w-md mx-auto mt-2 text-sm text-gray-600">
            <span>Address</span>
            <span>Payment</span>
            <span>Confirm</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Delivery Address */}
            {step === 1 && (
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Delivery Information</h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                      <input
                        type="text"
                        name="fullName"
                        required
                        value={formData.fullName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+254 7XX XXX XXX"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Method</label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, deliveryMethod: 'delivery' })}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          formData.deliveryMethod === 'delivery'
                            ? 'border-green-600 bg-green-50'
                            : 'border-gray-300'
                        }`}
                      >
                        <div className="text-2xl mb-2">🚚</div>
                        <div className="font-medium">Delivery</div>
                        <div className="text-xs text-gray-600">KSh 200</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, deliveryMethod: 'pickup' })}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          formData.deliveryMethod === 'pickup'
                            ? 'border-green-600 bg-green-50'
                            : 'border-gray-300'
                        }`}
                      >
                        <div className="text-2xl mb-2">🏪</div>
                        <div className="font-medium">Pickup</div>
                        <div className="text-xs text-gray-600">Free</div>
                      </button>
                    </div>
                  </div>

                  {formData.deliveryMethod === 'delivery' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Address</label>
                        <input
                          type="text"
                          name="address"
                          required
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="Street address, building, apartment"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">County</label>
                          <select
                            name="county"
                            required
                            value={formData.county}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          >
                            <option value="">Select County</option>
                            {counties.map(county => (
                              <option key={county} value={county}>{county}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Town/City</label>
                          <input
                            type="text"
                            name="town"
                            required
                            value={formData.town}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Order Notes (Optional)</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Any special instructions?"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>

                <button
                  onClick={() => setStep(2)}
                  className="w-full mt-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-bold"
                >
                  Continue to Payment
                </button>
              </div>
            )}

            {/* Step 2: Payment Method */}
            {step === 2 && (
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Method</h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentMethod: 'mpesa' })}
                      className={`p-6 border-2 rounded-lg transition-all ${
                        formData.paymentMethod === 'mpesa'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-300'
                      }`}
                    >
                      <div className="text-4xl mb-2">📱</div>
                      <div className="font-bold">M-Pesa</div>
                      <div className="text-xs text-gray-600">Lipa na M-Pesa</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentMethod: 'cash' })}
                      className={`p-6 border-2 rounded-lg transition-all ${
                        formData.paymentMethod === 'cash'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-300'
                      }`}
                    >
                      <div className="text-4xl mb-2">💵</div>
                      <div className="font-bold">Cash</div>
                      <div className="text-xs text-gray-600">Pay on delivery</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentMethod: 'card' })}
                      className={`p-6 border-2 rounded-lg transition-all ${
                        formData.paymentMethod === 'card'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-300'
                      }`}
                    >
                      <div className="text-4xl mb-2">💳</div>
                      <div className="font-bold">Card</div>
                      <div className="text-xs text-gray-600">Debit/Credit</div>
                    </button>
                  </div>

                  {formData.paymentMethod === 'mpesa' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        M-Pesa Phone Number
                      </label>
                      <input
                        type="tel"
                        name="mpesaPhone"
                        required
                        value={formData.mpesaPhone}
                        onChange={handleChange}
                        placeholder="+254 7XX XXX XXX"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        You'll receive an STK Push prompt on this number
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex space-x-4 mt-6">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-bold"
                  >
                    Review Order
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Order Review */}
            {step === 3 && (
              <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Review Your Order</h2>

                {/* Delivery Info */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-3">Delivery Information</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <p><strong>Name:</strong> {formData.fullName}</p>
                    <p><strong>Phone:</strong> {formData.phone}</p>
                    <p><strong>Method:</strong> {formData.deliveryMethod === 'delivery' ? '🚚 Delivery' : '🏪 Pickup'}</p>
                    {formData.deliveryMethod === 'delivery' && (
                      <>
                        <p><strong>Address:</strong> {formData.address}</p>
                        <p><strong>Location:</strong> {formData.town}, {formData.county}</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Payment Method */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-3">Payment Method</h3>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm">
                    <p>
                      {formData.paymentMethod === 'mpesa' && '📱 M-Pesa - ' + formData.mpesaPhone}
                      {formData.paymentMethod === 'cash' && '💵 Cash on Delivery'}
                      {formData.paymentMethod === 'card' && '💳 Debit/Credit Card'}
                    </p>
                  </div>
                </div>

                {/* Order Items */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-3">Order Items</h3>
                  <div className="space-y-2">
                    {items.map(item => (
                      <div key={item.product._id} className="flex justify-between items-center bg-gray-50 rounded-lg p-3">
                        <div className="flex-1">
                          <p className="font-medium">{item.product.name}</p>
                          <p className="text-sm text-gray-600">{item.quantity} × {formatPrice(item.product.price)}</p>
                        </div>
                        <p className="font-bold">{formatPrice(item.product.price * item.quantity)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => setStep(2)}
                    disabled={isProcessing}
                    className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium disabled:opacity-50"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handlePlaceOrder}
                    disabled={isProcessing}
                    className="flex-1 py-3 bg-gradient-to-r from-green-600 to-green-800 text-white rounded-lg hover:from-green-700 hover:to-green-900 transition-all font-bold disabled:opacity-50"
                  >
                    {isProcessing ? 'Processing...' : 'Place Order'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-200 sticky top-24">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h3>

              <div className="space-y-3 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-700">Subtotal</span>
                  <span className="font-medium">{formatPrice(totalAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Delivery</span>
                  <span className="font-medium">
                    {deliveryFee === 0 ? <span className="text-green-600">FREE</span> : formatPrice(deliveryFee)}
                  </span>
                </div>
                <div className="border-t pt-3 flex justify-between text-xl font-bold">
                  <span>Total</span>
                  <span className="text-green-600">{formatPrice(finalTotal)}</span>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4 text-sm text-green-800">
                <p className="font-medium mb-2">✓ Secure Checkout</p>
                <p className="text-xs">Your payment information is encrypted and secure</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;

